import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import { Card, message, Tooltip } from "antd";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import TurndownService from "turndown";
import { gfm } from "turndown-plugin-gfm";
import {
  X as CloseIcon,
  Copy as CopyIcon,
  Check as CheckIcon,
  Volume2 as SpeakIcon,
  Square as StopIcon,
  Reply as ReplyIcon,
  RotateCcw as ResetIcon,
} from "lucide-react";
import { EsterService } from "../../services/ester-service";
import SpeechRecognitionComponent from "../common/SpeechRecognitionComponent";
import {
  NAV_STYLE_MINI_SIDEBAR,
  NAV_STYLE_NO_HEADER_MINI_SIDEBAR,
  NAV_STYLE_DRAWER,
  TAB_SIZE,
} from "../../constants/ThemeSetting";
import "./style.css";

const turndownService = new TurndownService({ headingStyle: "atx", codeBlockStyle: "fenced" });
turndownService.use(gfm);

const WELCOME_MESSAGE = EsterService.getConfig().ester.welcomeMessage;

const makeWelcome = () => ({
  id: Date.now(),
  role: "assistant",
  content: WELCOME_MESSAGE,
  timestamp: new Date().toISOString(),
});

export const ESTER_SHORTCUTS = [
  "How do I create a visit?",
  "How do I cancel an appointment?",
  "How do I add a CC doctor?",
  "How do I reserve a slot in LineUp?",
  "MBS item number for ",
  "Fasting requirements for ",
  "Contrast precautions for ",
  "Referral requirements for ",
];

const getMessageNode = (messageId) =>
  document.querySelector(`[data-ester-message-id="${messageId}"]`);

const actionButtonStyle = {
  background: "none",
  border: "none",
  cursor: "pointer",
  padding: "4px",
  borderRadius: "6px",
  color: "#8c8c8c",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const md = {
  h1: { fontSize: "16px", fontWeight: "bold", margin: "12px 0 6px 0", color: "#1a1a1a" },
  h2: { fontSize: "15px", fontWeight: "bold", margin: "10px 0 5px 0", color: "#1a1a1a" },
  h3: { fontSize: "14px", fontWeight: "bold", margin: "8px 0 4px 0", color: "#1a1a1a" },
  h4: { fontSize: "13px", fontWeight: "bold", margin: "6px 0 3px 0", color: "#1a1a1a" },
  p: { margin: "6px 0", lineHeight: "1.6", fontSize: "13px" },
  ul: { margin: "6px 0", paddingLeft: "18px" },
  ol: { margin: "6px 0", paddingLeft: "18px" },
  li: { margin: "1px 0", lineHeight: "1.4" },
  strong: { fontWeight: "bold", color: "#1a1a1a" },
  em: { fontStyle: "italic" },
  code: { backgroundColor: "rgba(0,0,0,0.1)", padding: "1px 3px", borderRadius: "2px", fontSize: "12px", fontFamily: "monospace" },
  blockquote: { borderLeft: "2px solid #ddd", paddingLeft: "8px", margin: "6px 0", fontStyle: "italic", color: "#666" },
  table: { borderCollapse: "collapse", width: "100%", margin: "8px 0", fontSize: "12px" },
  th: { border: "1px solid #ddd", padding: "6px 8px", backgroundColor: "#f5f5f5", fontWeight: "bold", textAlign: "left", wordBreak: "normal", overflowWrap: "normal" },
  td: { border: "1px solid #ddd", padding: "6px 8px", textAlign: "left", wordBreak: "normal", overflowWrap: "normal" },
};

const assistantMarkdown = {
  h1: ({ node, ...props }) => <h1 style={md.h1} {...props} />,
  h2: ({ node, ...props }) => <h2 style={md.h2} {...props} />,
  h3: ({ node, ...props }) => <h3 style={md.h3} {...props} />,
  h4: ({ node, ...props }) => <h4 style={md.h4} {...props} />,
  p: ({ node, ...props }) => <p style={md.p} {...props} />,
  ul: ({ node, ...props }) => <ul style={md.ul} {...props} />,
  ol: ({ node, ...props }) => <ol style={md.ol} {...props} />,
  li: ({ node, ...props }) => <li style={md.li} {...props} />,
  strong: ({ node, ...props }) => <strong style={md.strong} {...props} />,
  em: ({ node, ...props }) => <em style={md.em} {...props} />,
  code: ({ node, ...props }) => <code style={md.code} {...props} />,
  a: ({ node, ...props }) => <a style={{ color: "#1890ff", textDecoration: "underline" }} target="_blank" rel="noopener noreferrer" {...props} />,
  hr: ({ node, ...props }) => <hr style={{ border: "none", borderTop: "1px solid #e0e0e0", height: 0, margin: "10px 0", background: "none" }} {...props} />,
  blockquote: ({ node, ...props }) => <blockquote style={md.blockquote} {...props} />,
  table: ({ node, ...props }) => (
    <div className="ester-table-scroll" style={{ overflowX: "auto", maxWidth: "100%" }}>
      <table style={md.table} {...props} />
    </div>
  ),
  th: ({ node, ...props }) => <th style={md.th} {...props} />,
  td: ({ node, ...props }) => <td style={md.td} {...props} />,
};

const userMarkdown = {
  h1: ({ node, ...props }) => <h1 style={{ fontSize: "15px", fontWeight: "bold", margin: "8px 0 4px 0", color: "inherit" }} {...props} />,
  h2: ({ node, ...props }) => <h2 style={{ fontSize: "14px", fontWeight: "bold", margin: "8px 0 4px 0", color: "inherit" }} {...props} />,
  h3: ({ node, ...props }) => <h3 style={{ fontSize: "14px", fontWeight: "bold", margin: "6px 0 3px 0", color: "inherit" }} {...props} />,
  p: ({ node, ...props }) => <p style={{ margin: "4px 0", lineHeight: "1.5", color: "inherit" }} {...props} />,
  ul: ({ node, ...props }) => <ul style={{ margin: "4px 0", paddingLeft: "18px", color: "inherit" }} {...props} />,
  ol: ({ node, ...props }) => <ol style={{ margin: "4px 0", paddingLeft: "18px", color: "inherit" }} {...props} />,
  li: ({ node, ...props }) => <li style={{ margin: "1px 0", lineHeight: "1.4", color: "inherit" }} {...props} />,
  strong: ({ node, ...props }) => <strong style={{ fontWeight: "bold", color: "inherit" }} {...props} />,
  em: ({ node, ...props }) => <em style={{ fontStyle: "italic", color: "inherit" }} {...props} />,
  code: ({ node, ...props }) => <code style={{ backgroundColor: "rgba(255,255,255,0.15)", padding: "1px 3px", borderRadius: "2px", fontSize: "12px", fontFamily: "monospace", color: "inherit" }} {...props} />,
  a: ({ node, ...props }) => <a style={{ color: "#91d5ff", textDecoration: "underline" }} target="_blank" rel="noopener noreferrer" {...props} />,
  blockquote: ({ node, ...props }) => <blockquote style={{ borderLeft: "3px solid rgba(255,255,255,0.45)", paddingLeft: "8px", margin: "4px 0", opacity: 0.8, color: "inherit" }} {...props} />,
  table: ({ node, ...props }) => (
    <div className="ester-table-scroll" style={{ overflowX: "auto", maxWidth: "100%" }}>
      <table style={{ borderCollapse: "collapse", width: "100%", margin: "6px 0", fontSize: "12px" }} {...props} />
    </div>
  ),
  th: ({ node, ...props }) => <th style={{ border: "1px solid rgba(255,255,255,0.4)", padding: "4px 6px", backgroundColor: "rgba(255,255,255,0.1)", fontWeight: "bold", textAlign: "left", color: "inherit" }} {...props} />,
  td: ({ node, ...props }) => <td style={{ border: "1px solid rgba(255,255,255,0.4)", padding: "4px 6px", textAlign: "left", color: "inherit" }} {...props} />,
};

const ChatShortcuts = ({ onPick, disabled }) => (
  <div className="ester-shortcuts" aria-label="Quick prompts">
    <div className="ester-shortcuts-track">
      {[...ESTER_SHORTCUTS, ...ESTER_SHORTCUTS].map((text, i) => (
        <button
          key={i}
          type="button"
          className="ester-shortcut-chip"
          tabIndex={i < ESTER_SHORTCUTS.length ? 0 : -1}
          disabled={disabled}
          onClick={() => onPick && onPick(text)}
        >
          {text.trim()}
        </button>
      ))}
    </div>
  </div>
);

const MessageContent = React.memo(
  ({ message, liveContent, isCopied, isSpeaking, onCopy, onReadAloud, onReply, statusText }) => {
    const bubble = {
      maxWidth: "85%",
      padding: "12px 16px",
      borderRadius: "16px",
      fontSize: "14px",
      lineHeight: "1.4",
      wordBreak: "break-word",
      backgroundColor: message.isError ? "#ffebee" : message.role === "user" ? "#2c2c2c" : "#f0f0f0",
      color: message.isError ? "#c62828" : message.role === "user" ? "white" : "#262626",
      border: message.isError ? "1px solid #ffcdd2" : "none",
    };

    if (message.role === "assistant" && !message.isError) {
      const content = liveContent != null ? liveContent : message.content;

      if (message.isStreaming && !content) {
        return (
          <div style={{ padding: "6px 8px" }}>
            <span className="ester-status-shimmer">{statusText || "Thinking..."}</span>
          </div>
        );
      }

      return (
        <div style={{ display: "flex", flexDirection: "column", maxWidth: "85%" }}>
          <div style={{ ...bubble, maxWidth: "100%" }}>
            <div data-ester-message-id={message.id} style={{ position: "relative", display: "inline-block", width: "100%" }}>
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={assistantMarkdown}>
                {content}
              </ReactMarkdown>
              {message.isStreaming && <span className="ester-streaming-cursor">|</span>}
            </div>
          </div>
          {!message.isStreaming && (
            <div style={{ display: "flex", gap: "2px", marginTop: "2px", paddingLeft: "6px" }}>
              <Tooltip title={isCopied ? "Copied!" : "Copy"} mouseEnterDelay={0.2} placement="bottom" overlayClassName="ester-chat-tooltip">
                <button type="button" onClick={() => onCopy(message)} aria-label="Copy message" style={actionButtonStyle}>
                  {isCopied ? <CheckIcon size={14} color="#28a745" /> : <CopyIcon size={14} />}
                </button>
              </Tooltip>
              <Tooltip title={isSpeaking ? "Stop reading" : "Read aloud"} mouseEnterDelay={0.2} placement="bottom" overlayClassName="ester-chat-tooltip">
                <button
                  type="button"
                  onClick={() => onReadAloud(message)}
                  aria-label={isSpeaking ? "Stop reading" : "Read message aloud"}
                  style={{ ...actionButtonStyle, color: isSpeaking ? "#1890ff" : "#8c8c8c" }}
                >
                  {isSpeaking ? <StopIcon size={14} /> : <SpeakIcon size={14} />}
                </button>
              </Tooltip>
              <Tooltip title="Reply to this message" mouseEnterDelay={0.2} placement="bottom" overlayClassName="ester-chat-tooltip">
                <button type="button" onClick={() => onReply(message)} aria-label="Reply to this message" style={actionButtonStyle}>
                  <ReplyIcon size={14} />
                </button>
              </Tooltip>
            </div>
          )}
        </div>
      );
    }

    if (message.role === "user" && !message.isError) {
      return (
        <div style={bubble}>
          {message.replyPreview && (
            <div
              style={{
                borderLeft: "3px solid rgba(255,255,255,0.45)",
                paddingLeft: "8px",
                marginBottom: "6px",
                fontSize: "12px",
                opacity: 0.75,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {message.replyPreview}
            </div>
          )}
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={userMarkdown}>
            {message.content}
          </ReactMarkdown>
        </div>
      );
    }

    return <div style={{ ...bubble, whiteSpace: "pre-wrap" }}>{message.content}</div>;
  }
);

const EsterChatModal = ({ isOpen, onClose }) => {
  const { navStyle, width, navCollapsed } = useSelector((state) => state.settings);

  const sidebarWidth = (() => {
    if (width < TAB_SIZE || navStyle === NAV_STYLE_DRAWER) return navCollapsed ? 280 : 0;
    const collapsed = navStyle === NAV_STYLE_MINI_SIDEBAR || navStyle === NAV_STYLE_NO_HEADER_MINI_SIDEBAR;
    return collapsed ? 80 : 280;
  })();

  const [messages, setMessages] = useState([makeWelcome()]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [chatSession, setChatSession] = useState(null);
  const [streamingMessageId, setStreamingMessageId] = useState(null);
  const [streamingContent, setStreamingContent] = useState("");
  const [streamingStatus, setStreamingStatus] = useState(null);
  const [copiedMessageId, setCopiedMessageId] = useState(null);
  const [speakingMessageId, setSpeakingMessageId] = useState(null);
  const [replyTo, setReplyTo] = useState(null);
  const [resetTranscriptFn, setResetTranscriptFn] = useState(null);

  const chatContainerRef = useRef(null);
  const inputRef = useRef(null);
  const nearBottomRef = useRef(true);
  const followAnimRef = useRef(null);
  const streamingRef = useRef(null);
  const INPUT_MAX_HEIGHT = 112;

  const startFollowLoop = () => {
    if (followAnimRef.current) return;
    const step = () => {
      followAnimRef.current = null;
      const el = chatContainerRef.current;
      if (!el || !nearBottomRef.current) return;
      const delta = el.scrollHeight - el.clientHeight - el.scrollTop;
      if (delta > 1) {
        el.scrollTop += Math.max(1, delta * 0.2);
        followAnimRef.current = requestAnimationFrame(step);
      }
    };
    followAnimRef.current = requestAnimationFrame(step);
  };

  useEffect(() => {
    if (nearBottomRef.current) startFollowLoop();
  }, [messages, streamingContent]);

  useEffect(
    () => () => {
      if (followAnimRef.current) cancelAnimationFrame(followAnimRef.current);
      streamingRef.current?.abort();
      if (window.speechSynthesis) window.speechSynthesis.cancel();
    },
    []
  );

  const handleChatScroll = () => {
    const el = chatContainerRef.current;
    if (el && el.scrollHeight - el.scrollTop - el.clientHeight < 40) nearBottomRef.current = true;
  };
  const handleChatWheel = (e) => {
    if (e.deltaY < 0) nearBottomRef.current = false;
  };

  useEffect(() => {
    if (!isOpen) return undefined;
    const validation = EsterService.validateConfig();
    if (!validation.valid) message.error(validation.message, 10);

    const onEsc = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen && window.speechSynthesis) {
      window.speechSynthesis.cancel();
      setSpeakingMessageId(null);
    }
  }, [isOpen]);

  const handleCopyMessage = useCallback(async (msg) => {
    const node = getMessageNode(msg.id);
    const html = node ? node.innerHTML : msg.content;
    const plain = EsterService.markdownToPlainText(msg.content);
    try {
      if (navigator.clipboard && window.ClipboardItem) {
        await navigator.clipboard.write([
          new window.ClipboardItem({
            "text/html": new Blob([html], { type: "text/html" }),
            "text/plain": new Blob([plain], { type: "text/plain" }),
          }),
        ]);
      } else {
        await navigator.clipboard.writeText(plain);
      }
      setCopiedMessageId(msg.id);
      setTimeout(() => setCopiedMessageId(null), 2000);
    } catch (e) {
      message.error("Could not copy message");
    }
  }, []);

  const handleReadAloud = useCallback(
    (msg) => {
      if (!window.speechSynthesis) {
        message.error("Read aloud is not supported in this browser");
        return;
      }
      if (speakingMessageId === msg.id) {
        window.speechSynthesis.cancel();
        setSpeakingMessageId(null);
        return;
      }
      window.speechSynthesis.cancel();
      const node = getMessageNode(msg.id);
      const utterance = new SpeechSynthesisUtterance(node ? node.innerText : msg.content);
      utterance.lang = "en-AU";
      utterance.onend = () => setSpeakingMessageId(null);
      utterance.onerror = () => setSpeakingMessageId(null);
      setSpeakingMessageId(msg.id);
      window.speechSynthesis.speak(utterance);
    },
    [speakingMessageId]
  );

  const handleReplyTo = useCallback((msg) => {
    setReplyTo({
      id: msg.id,
      content: msg.content,
      preview: EsterService.markdownToPlainText(msg.content).replace(/\s+/g, " ").slice(0, 120),
    });
    inputRef.current?.focus();
  }, []);

  const handleShortcutPick = (text) => {
    const el = inputRef.current;
    if (!el) return;
    const current = el.innerText.replace(/ /g, " ").trim();
    el.innerText = current ? `${current} ${text}` : text;
    setInputValue(el.innerText);
    el.focus();
    const range = document.createRange();
    range.selectNodeContents(el);
    range.collapse(false);
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(range);
  };

  const handleClearChat = () => {
    streamingRef.current?.abort();
    setMessages([makeWelcome()]);
    setChatSession(null);
    setReplyTo(null);
    setInputValue("");
    if (inputRef.current) inputRef.current.innerHTML = "";
    message.success("Chat cleared");
  };

  // ---------------------------------------------------------- sending

  const finishStream = (aiMessageId, content, isError) => {
    streamingRef.current = null;
    setMessages((prev) =>
      prev.map((m) => (m.id === aiMessageId ? { ...m, content, isStreaming: false, ...(isError ? { isError: true } : {}) } : m))
    );
    setStreamingMessageId(null);
    setStreamingContent("");
    setStreamingStatus(null);
    setIsLoading(false);
  };

  const sendChatMessage = async (userMessage, activeReply) => {
    setIsLoading(true);
    nearBottomRef.current = true;
    setMessages((prev) => [
      ...prev,
      {
        id: Date.now(),
        role: "user",
        content: userMessage,
        replyPreview: activeReply ? activeReply.preview : null,
        timestamp: new Date().toISOString(),
      },
    ]);
    
    const outgoing = activeReply
      ? `Regarding your earlier answer:\n\n> ${activeReply.content.replace(/\n/g, "\n> ")}\n\n${userMessage}`
      : userMessage;

    const aiMessageId = Date.now() + 1;

    try {
      let session = chatSession;
      if (!session?.conversationId) {
        session = await EsterService.createConversation();
        setChatSession(session);
      }

      setMessages((prev) => [
        ...prev,
        { id: aiMessageId, role: "assistant", content: "", timestamp: new Date().toISOString(), isStreaming: true },
      ]);
      setStreamingMessageId(aiMessageId);
      setStreamingStatus("Thinking...");
      setIsLoading(false);

      const controller = new AbortController();
      streamingRef.current = controller;
      const timeoutMs = EsterService.getConfig().api.timeout || 60000;
      let idleTimer = setTimeout(() => controller.abort(), timeoutMs);
      const resetIdle = () => {
        clearTimeout(idleTimer);
        idleTimer = setTimeout(() => controller.abort(), timeoutMs);
      };

      let accumulated = "";
      await EsterService.sendMessageStream(session.conversationId, outgoing, {
        signal: controller.signal,
        onStatus: (status) => {
          if (!accumulated) setStreamingStatus(status);
        },
        onChunk: (chunk) => {
          resetIdle();
          if (!accumulated) setStreamingStatus(null);
          accumulated += chunk;
          setStreamingContent(EsterService.stripCitationMarkers(accumulated));
        },
        onComplete: () => {
          clearTimeout(idleTimer);
          const finalText = EsterService.stripCitationMarkers(accumulated).trim();
          if (finalText) finishStream(aiMessageId, finalText, false);
          else finishStream(aiMessageId, "I didn't get a response. Please try again in a moment.", true);
        },
        onError: (error) => {
          clearTimeout(idleTimer);
          finishStream(aiMessageId, `I'm having trouble connecting right now. ${error.message || "Please try again in a moment."}`, true);
          message.error("Unable to get a response from Ester. Please try again.");
        },
      });
    } catch (error) {
      setMessages((prev) => [
        ...prev.filter((m) => m.id !== aiMessageId),
        {
          id: Date.now() + 2,
          role: "assistant",
          content: `I'm having trouble connecting right now. ${error.message}`,
          timestamp: new Date().toISOString(),
          isError: true,
        },
      ]);
      setStreamingMessageId(null);
      setStreamingStatus(null);
      setIsLoading(false);
      message.error("Unable to get a response from Ester. Please try again.");
    }
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading || streamingMessageId) return;
    const el = inputRef.current;
    let userMessage;
    try {
      userMessage = el ? turndownService.turndown(el.innerHTML).trim() : inputValue.trim();
    } catch (e) {
      userMessage = (el ? el.innerText : inputValue).trim();
    }
    if (!userMessage) return;

    setInputValue("");
    if (el) el.innerHTML = "";
    if (resetTranscriptFn) resetTranscriptFn();
    const activeReply = replyTo;
    setReplyTo(null);
    await sendChatMessage(userMessage, activeReply);
  };

  const handleTranscript = (transcript) => {
    if (!transcript || !transcript.trim()) return;
    setInputValue(transcript);
    const el = inputRef.current;
    if (el && el.innerText !== transcript) el.innerText = transcript;
  };

  const isInputDisabled = isLoading || !!streamingMessageId;

  if (!isOpen) return null;

  return (
    <Card
      className="ester-chat-modal"
      style={{
        position: "fixed",
        bottom: 0,
        left: `${sidebarWidth}px`,
        borderBottomLeftRadius: 0,
        transition: "left 0.25s ease",
        width: "500px",
        maxWidth: "calc(100vw - 120px)",
        height: "560px",
        maxHeight: "100vh",
        zIndex: 1001,
        display: "flex",
        flexDirection: "column",
        padding: 0,
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
        borderRadius: "16px",
        border: "1px solid #e8e8e8",
        overflow: "hidden",
        backgroundColor: "#fafafa",
      }}
      bodyStyle={{ padding: 0, height: "100%", display: "flex", flexDirection: "column" }}
    >
      {/* Header */}
      <div className="ester-chat-header">
        <div style={{ display: "flex", alignItems: "center" }}>
          <div className="ester-avatar-container">
            <img src="/Ester.png" alt="Ester" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          </div>
          <div>
            <div style={{ fontWeight: 600, fontSize: "16px", color: "#262626", lineHeight: 1.2 }}>Ester</div>
            <div style={{ fontSize: "12px", color: "#8c8c8c", lineHeight: 1.2 }}>ClarityAI Assistant</div>
          </div>
        </div>
        <div style={{ display: "flex", gap: "4px" }}>
          {messages.length > 1 && (
            <Tooltip title="New chat" mouseEnterDelay={0.2} placement="bottom" overlayClassName="ester-chat-tooltip">
              <button type="button" className="ester-header-button" onClick={handleClearChat} aria-label="Start a new chat">
                <ResetIcon size={18} />
              </button>
            </Tooltip>
          )}
          <button type="button" className="ester-header-button" onClick={onClose} aria-label="Close chat">
            <CloseIcon size={20} />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div
        className="ester-messages-container"
        ref={chatContainerRef}
        onScroll={handleChatScroll}
        onWheel={handleChatWheel}
      >
        {messages.map((m) => (
          <div key={m.id} style={{ marginBottom: "16px", display: "flex", justifyContent: m.role === "user" ? "flex-end" : "flex-start" }}>
            <MessageContent
              message={m}
              liveContent={m.isStreaming && m.id === streamingMessageId ? streamingContent : null}
              isCopied={copiedMessageId === m.id}
              isSpeaking={speakingMessageId === m.id}
              onCopy={handleCopyMessage}
              onReadAloud={handleReadAloud}
              onReply={handleReplyTo}
              statusText={m.isStreaming && m.id === streamingMessageId ? streamingStatus : null}
            />
          </div>
        ))}
        {isLoading && !streamingMessageId && (
          <div style={{ padding: "6px 8px", marginBottom: "16px" }}>
            <span className="ester-status-shimmer">Connecting...</span>
          </div>
        )}
      </div>

      {/* Input */}
      <div className="ester-input-container">
        {replyTo && (
          <div className="ester-reply-banner">
            <ReplyIcon size={13} style={{ flexShrink: 0, color: "#8c8c8c" }} />
            <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{replyTo.preview}</span>
            <button type="button" onClick={() => setReplyTo(null)} aria-label="Cancel reply" style={{ ...actionButtonStyle, padding: "2px" }}>
              <CloseIcon size={13} />
            </button>
          </div>
        )}
        {messages.length <= 1 && <ChatShortcuts onPick={handleShortcutPick} disabled={isInputDisabled} />}
        <div className="ester-input-wrapper">
          <div
            ref={inputRef}
            className="ester-rich-input"
            contentEditable={!isInputDisabled}
            suppressContentEditableWarning
            role="textbox"
            aria-multiline="true"
            aria-label="Ask Ester"
            data-placeholder={streamingMessageId ? "Ester is responding..." : isLoading ? "Ester is connecting..." : "Ask Ester"}
            onInput={(e) => setInputValue(e.currentTarget.innerText)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey && !isInputDisabled) {
                e.preventDefault();
                handleSendMessage();
              }
            }}
            style={{
              maxHeight: `${INPUT_MAX_HEIGHT}px`,
              opacity: isInputDisabled ? 0.6 : 1,
              cursor: isInputDisabled ? "not-allowed" : "text",
            }}
          />
          <SpeechRecognitionComponent onTranscript={handleTranscript} onResetTranscript={setResetTranscriptFn} iconSize={20} />
          <button
            type="button"
            className="ester-send-button"
            onClick={handleSendMessage}
            disabled={!inputValue.trim() || isInputDisabled}
            aria-label="Send"
          >
            {isLoading ? (
              <div className="ester-loading-spinner" />
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
              </svg>
            )}
          </button>
        </div>
        {messages.length > 1 && (
          <div className="ester-disclaimer">Ester is AI and can make mistakes. Please double-check responses.</div>
        )}
      </div>
    </Card>
  );
};

export default EsterChatModal;
